import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { flattenPages, nextCursorParam } from '@/hooks/queries/pagination';
import {
  getBrokerClients,
  getBrokerDeposits,
  getBrokerDetail,
  getBrokerFeeBands,
  getBrokerTrades,
  getBrokerTradingStats,
  getBrokerTransactionBatches,
  getBrokerTransfers,
  getBrokerWithdrawals,
  type PaginatedResponse,
  type WireBrokerTrade,
  type WireBrokerTransaction,
  type WireBrokerTransfer,
  type WireClientAccount,
  type WireFeeBand,
  type WireTransactionBatch,
} from '@/services/api/brokers';
import { BROKERS_QUERY_KEY } from './use-brokers';

// Key layout: ['brokers', brokerId] for the broker itself,
// ['brokers', brokerId, <segment>] for each tab dataset. The list page owns
// ['brokers', 'list'] (use-brokers.ts) — a brokerId is a UUID so the keys never clash.
export const brokerDetailQueryKey = (brokerId: string) => [...BROKERS_QUERY_KEY, brokerId] as const;
export const brokerTradingStatsQueryKey = (brokerId: string) => [...brokerDetailQueryKey(brokerId), 'trading-stats'] as const;

// Stable module-level selects via flattenPages (pagination.ts) — the
// select-memoization rationale and the malformed-page `?? []` guard live there.
// One instantiation per dataset keeps each select reference stable across renders.
const selectFeeBands = flattenPages((page: PaginatedResponse<WireFeeBand>) => page.items);
const selectClients = flattenPages((page: PaginatedResponse<WireClientAccount>) => page.items);
const selectTrades = flattenPages((page: PaginatedResponse<WireBrokerTrade>) => page.items);
const selectTransactions = flattenPages((page: PaginatedResponse<WireBrokerTransaction>) => page.items);
const selectTransfers = flattenPages((page: PaginatedResponse<WireBrokerTransfer>) => page.items);
const selectTransactionBatches = flattenPages((page: PaginatedResponse<WireTransactionBatch>) => page.items);

export const brokerDetailQueryOptions = (brokerId: string) =>
  queryOptions({
    queryKey: brokerDetailQueryKey(brokerId),
    queryFn: () => getBrokerDetail(brokerId),
  });

/** 404 'BROKER_NOT_FOUND' branches to the not-found panel; other failures get Retry. */
export const useBrokerDetail = (brokerId: string) => useQuery(brokerDetailQueryOptions(brokerId));

/**
 * Slow endpoint — up to ~29 s server-side. Mounted in
 * parallel with the trades query on Trades-tab activation; each keeps independent
 * loading/error state so slow stats never blank the trades table.
 */
export const brokerTradingStatsQueryOptions = (brokerId: string) =>
  queryOptions({
    queryKey: brokerTradingStatsQueryKey(brokerId),
    queryFn: () => getBrokerTradingStats(brokerId),
  });

export const useBrokerTradingStats = (brokerId: string) => useQuery(brokerTradingStatsQueryOptions(brokerId));

// Every list below: limit=25 per page (service constant), Load More passes the last
// cursor and APPENDS items; cursor omitted by the API when exhausted.
// Lazy-load-on-first-activation comes from mount timing: each tab/segment component
// mounts its hook on first activation, and the query cache (5-min staleTime) keeps
// revisits from refetching — the legacy empty-tab refetch quirk disappears.

export const brokerFeeBandsInfiniteQueryOptions = (brokerId: string) =>
  infiniteQueryOptions({
    queryKey: [...brokerDetailQueryKey(brokerId), 'fee-bands'] as const,
    queryFn: ({ pageParam }) => getBrokerFeeBands(brokerId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursorParam,
    select: selectFeeBands,
  });

export const useBrokerFeeBands = (brokerId: string) => useInfiniteQuery(brokerFeeBandsInfiniteQueryOptions(brokerId));

export const brokerClientsInfiniteQueryOptions = (brokerId: string) =>
  infiniteQueryOptions({
    queryKey: [...brokerDetailQueryKey(brokerId), 'clients'] as const,
    queryFn: ({ pageParam }) => getBrokerClients(brokerId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursorParam,
    select: selectClients,
  });

export const useBrokerClients = (brokerId: string) => useInfiniteQuery(brokerClientsInfiniteQueryOptions(brokerId));

export const brokerTradesInfiniteQueryOptions = (brokerId: string) =>
  infiniteQueryOptions({
    queryKey: [...brokerDetailQueryKey(brokerId), 'trades'] as const,
    queryFn: ({ pageParam }) => getBrokerTrades(brokerId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursorParam,
    select: selectTrades,
  });

export const useBrokerTrades = (brokerId: string) => useInfiniteQuery(brokerTradesInfiniteQueryOptions(brokerId));

export const brokerDepositsInfiniteQueryOptions = (brokerId: string) =>
  infiniteQueryOptions({
    queryKey: [...brokerDetailQueryKey(brokerId), 'deposits'] as const,
    queryFn: ({ pageParam }) => getBrokerDeposits(brokerId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursorParam,
    select: selectTransactions,
  });

export const useBrokerDeposits = (brokerId: string) => useInfiniteQuery(brokerDepositsInfiniteQueryOptions(brokerId));

export const brokerWithdrawalsInfiniteQueryOptions = (brokerId: string) =>
  infiniteQueryOptions({
    queryKey: [...brokerDetailQueryKey(brokerId), 'withdrawals'] as const,
    queryFn: ({ pageParam }) => getBrokerWithdrawals(brokerId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursorParam,
    select: selectTransactions,
  });

export const useBrokerWithdrawals = (brokerId: string) => useInfiniteQuery(brokerWithdrawalsInfiniteQueryOptions(brokerId));

export const brokerTransfersInfiniteQueryOptions = (brokerId: string) =>
  infiniteQueryOptions({
    queryKey: [...brokerDetailQueryKey(brokerId), 'transfers'] as const,
    queryFn: ({ pageParam }) => getBrokerTransfers(brokerId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursorParam,
    select: selectTransfers,
  });

export const useBrokerTransfers = (brokerId: string) => useInfiniteQuery(brokerTransfersInfiniteQueryOptions(brokerId));

export const brokerTransactionBatchesInfiniteQueryOptions = (brokerId: string) =>
  infiniteQueryOptions({
    queryKey: [...brokerDetailQueryKey(brokerId), 'transaction-batches'] as const,
    queryFn: ({ pageParam }) => getBrokerTransactionBatches(brokerId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursorParam,
    select: selectTransactionBatches,
  });

export const useBrokerTransactionBatches = (brokerId: string) => useInfiniteQuery(brokerTransactionBatchesInfiniteQueryOptions(brokerId));

/** Deposits and withdrawals share a wire shape — and therefore a result type. */
export type BrokerTransactionsQueryResult = ReturnType<typeof useBrokerDeposits>;
